import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ImageThumb from "@/components/ImageThumb";
import { ArrowUpRight, ChevronRight, ShoppingBag, Watch, Footprints, Search, Heart, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import TrustSpotlight from "@/components/TrustSpotlight";
import RecentlyViewed from "@/components/RecentlyViewed";
import StillInterested from "@/components/StillInterested";

// Cover triptych — one wardrobe piece per era, shot clean & catalog-style
const ERAS = [
  {
    year: "1980s",
    caption: "Hand-knit fair isle",
    img: "https://static.prod-images.emergentagent.com/jobs/b4be43f3-2601-441c-9078-02b7f7e5431f/images/efdbdcfacbffb057eb0448b67f67a9479a56cb6368fcc81e04d86a14a1da4a9a.jpeg",
    alt: "Vintage 1980s women's hand-knit fair isle sweater",
    to: "/browse?condition=used",
    testid: "era-80s",
  },
  {
    year: "1990s",
    caption: "Double denim, done right",
    img: "https://static.prod-images.emergentagent.com/jobs/b4be43f3-2601-441c-9078-02b7f7e5431f/images/d70434d6965797f0a1c22d7ade47fe1a955da36980a08b9e69d8f8c5ead5eb1a.jpeg",
    alt: "1990s women's denim jacket, mini skirt and shoulder bag on a dress form",
    to: "/browse?condition=used",
    testid: "era-90s",
  },
  {
    year: "2026",
    caption: "Quiet luxury tailoring",
    img: "https://static.prod-images.emergentagent.com/jobs/b4be43f3-2601-441c-9078-02b7f7e5431f/images/02208c8a5992db9174720525b86b61211f04c2f849a6e7875cabea767da057bb.jpeg",
    alt: "2026 women's overcoat, knit top and wide-leg trousers on a dress form",
    to: "/browse?condition=new",
    testid: "era-now",
  },
];

const COVER_ICONS = [
  { Icon: ShoppingBag, label: "Handbags", to: "/browse?product_type=handbags" },
  { Icon: Watch, label: "Watches", to: "/browse?product_type=watches" },
  { Icon: Footprints, label: "Shoes", to: "/browse?product_type=shoes" },
];

const CATEGORY_TILES = [
  { key: "women", label: "Women", to: "/browse?product_type=apparel&dept=women", img: "https://images.unsplash.com/photo-1633381521050-26bb467d9d5a?w=800" },
  { key: "men", label: "Men", to: "/browse?product_type=apparel&dept=men", img: "https://images.pexels.com/photos/38290948/pexels-photo-38290948.jpeg?w=800" },
  { key: "kids", label: "Kids", to: "/browse?product_type=apparel&dept=kids", img: "https://images.pexels.com/photos/15395266/pexels-photo-15395266.jpeg?w=800" },
  { key: "shoes", label: "Shoes", to: "/browse?product_type=shoes", img: "https://images.pexels.com/photos/4061385/pexels-photo-4061385.jpeg?w=800" },
  { key: "handbags", label: "Handbags", to: "/browse?product_type=handbags", img: "https://images.unsplash.com/photo-1584917865442-de89df76afd3?w=800" },
  { key: "wallets", label: "Wallets", to: "/browse?product_type=wallets", img: "https://images.unsplash.com/photo-1579014134953-1580d7f123f3?w=800" },
  { key: "hats", label: "Hats", to: "/browse?product_type=hats", img: "https://images.unsplash.com/photo-1521369909029-2afed882baee?w=800" },
  { key: "watches", label: "Watches", to: "/browse?product_type=watches", img: "https://images.unsplash.com/photo-1604727199378-bf5dd08726ad?w=800&q=80" },
  { key: "necklaces", label: "Necklaces", to: "/browse?product_type=necklaces", img: "https://images.unsplash.com/photo-1599643477877-530eb83abc8e?w=800" },
  { key: "earrings", label: "Earrings", to: "/browse?product_type=earrings", img: "https://images.unsplash.com/photo-1535632066927-ab7c9ab60908?w=800" },
  { key: "bracelets", label: "Bracelets", to: "/browse?product_type=bracelets", img: "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=800" },
  { key: "rings", label: "Rings", to: "/browse?product_type=rings", img: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=800" },
];

export default function Landing() {
  const [showcase, setShowcase] = useState([]);
  const [hero, setHero] = useState([]);
  const [promoOpen, setPromoOpen] = useState(false);
  const [trending, setTrending] = useState([]);
  const [catPreview, setCatPreview] = useState({});
  const [search, setSearch] = useState("");
  const nav = useNavigate();
  useEffect(() => {
    api.get("/home-showcase", { params: { limit: 20 } }).then(r => setShowcase(r.data)).catch(() => {});
    api.get("/home-trending", { params: { limit: 8 } }).then(r => setTrending(r.data)).catch(() => {});
    api.get("/category-previews").then(r => {
      const m = {};
      (r.data || []).forEach((p) => { m[p.key] = p; });
      setCatPreview(m);
    }).catch(() => {});
  }, []);

  // Rotate the three cover picks periodically — paid/featured items stay pinned, the rest re-shuffle.
  useEffect(() => {
    const loadHero = () => api.get("/home-hero", { params: { limit: 3 } }).then(r => setHero(r.data)).catch(() => {});
    loadHero();
    const t = setInterval(loadHero, 9000);
    return () => clearInterval(t);
  }, []);

  // The three cover spots: real listings at random (paid/featured first), padding with
  // the era cover art only when the catalogue can't fill all three.
  const heroPanels = [0, 1, 2].map((i) => (
    hero[i] ? { type: "real", it: hero[i] } : { type: "stock", e: ERAS[i] }
  ));

  const submitSearch = (e) => {
    e.preventDefault();
    const term = search.trim();
    nav(term ? `/browse?q=${encodeURIComponent(term)}` : "/browse");
  };

  return (
    <div data-testid="landing-page">
      {/* HERO — The cover: Vintage, Reimagined */}
      <section className="relative" style={{ background: "#F0EBE1" }} data-testid="hero-cover">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none paper-grain opacity-[0.07] mix-blend-multiply" />

        <div className="relative max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-10 py-5 lg:py-8">
          <div className="cover-rise p-[5px]" style={{ border: "1px solid rgba(30,58,43,0.35)" }}>
            <div style={{ border: "1px solid rgba(30,58,43,0.22)", background: "#F9F6F0" }}>

              {/* Masthead */}
              <div className="px-5 sm:px-8 pt-6 pb-5 rise-2" style={{ borderBottom: "1px solid rgba(30,58,43,0.2)" }}>
                <div className="flex items-center gap-4">
                  <span className="h-px flex-1" style={{ background: "rgba(30,58,43,0.28)" }} />
                  <svg width="46" height="14" viewBox="0 0 46 14" fill="none" aria-hidden="true" className="shrink-0">
                    <path d="M23 1.5 27 7l-4 5.5L19 7l4-5.5Z" stroke="#697169" strokeWidth="1" />
                    <path d="M19 7H7m0 0 4-3M7 7l4 3M27 7h12m0 0-4-3m4 3-4 3" stroke="#697169" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                  <span className="h-px flex-1" style={{ background: "rgba(30,58,43,0.28)" }} />
                </div>
                <h1 className="font-cover font-extrabold text-center leading-[0.95] mt-4 text-[13vw] sm:text-6xl lg:text-[5.4rem]" style={{ color: "#1A201A" }}>
                  Vintage, Reimagined
                </h1>
                <p className="mt-3 text-center label-eyebrow" style={{ color: "#697169" }}>
                  Est. Style · 1980 &nbsp;—&nbsp; 2026
                </p>
              </div>

              {/* Triptych */}
              <div className="relative grid grid-cols-3">
                {heroPanels.map((p, i) => {
                  const isReal = p.type === "real";
                  const it = p.it;
                  const to = isReal ? `/product/${it.id}` : p.e.to;
                  const caption = isReal ? it.title : p.e.caption;
                  return (
                    <Link
                      key={isReal ? it.id : p.e.year}
                      to={to}
                      data-testid={isReal ? `hero-item-${it.id}` : p.e.testid}
                      className={`era-panel group relative block px-3 sm:px-6 pt-6 pb-7 rise-${i + 3}`}
                      style={{
                        background: i === 1 ? "#F0EBE1" : "transparent",
                        borderLeft: i ? "1px solid rgba(30,58,43,0.14)" : "none",
                      }}
                    >
                      <div className="relative overflow-hidden" style={{ background: "#E5DFD2" }}>
                        {isReal ? (
                          <ImageThumb path={it.image_paths?.[0]} alt={it.title}
                            className="w-full h-[30vh] min-h-[190px] sm:h-[34vh] lg:h-[40vh] lg:max-h-[400px] object-cover" />
                        ) : (
                          <img src={p.e.img} alt={p.e.alt} loading={i === 0 ? "eager" : "lazy"}
                            className="w-full h-[30vh] min-h-[190px] sm:h-[34vh] lg:h-[40vh] lg:max-h-[400px] object-cover" />
                        )}
                        {isReal && it.boosted && (
                          <span className="absolute left-2 top-2 px-1.5 py-0.5 text-[0.55rem] uppercase tracking-[0.16em] font-medium"
                            style={{ background: "#2563EB", color: "#fff" }} data-testid={`hero-featured-${it.id}`}>Featured</span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <span className="text-[0.7rem] sm:text-xs tracking-[0.14em] uppercase truncate" style={{ color: "#697169" }}>{caption}</span>
                        {isReal ? (
                          <span className="text-[0.7rem] sm:text-xs font-medium shrink-0" style={{ color: "#1A201A" }} data-testid={`hero-price-${it.id}`}>${Number(it.price).toFixed(2)}</span>
                        ) : (
                          <ArrowUpRight size={14} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: "#1E3A2B" }} />
                        )}
                      </div>
                    </Link>
                  );
                })}

                {/* Era connectors */}
                {[1, 2].map((n) => (
                  <span
                    key={n}
                    aria-hidden="true"
                    className="hidden sm:flex absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full items-center justify-center pointer-events-none"
                    style={{ left: `${(n * 100) / 3}%`, background: "#F9F6F0", border: "1px solid rgba(30,58,43,0.25)", boxShadow: "0 2px 10px rgba(30,58,43,0.12)" }}
                  >
                    <ChevronRight size={14} style={{ color: "#1E3A2B" }} />
                  </span>
                ))}
              </div>

              {/* Footer band */}
              <div className="px-5 sm:px-8 py-5 flex flex-col lg:flex-row items-center gap-5 lg:gap-8 rise-6" style={{ borderTop: "1px solid rgba(30,58,43,0.2)", background: "#EAE4D8" }}>
                <div className="flex items-center gap-5 sm:gap-7">
                  {COVER_ICONS.map(({ Icon, label, to }) => (
                    <Link key={label} to={to} data-testid={`cover-icon-${label.toLowerCase()}`}
                      className="flex flex-col items-center gap-1.5 group">
                      <Icon size={26} strokeWidth={1} style={{ color: "#697169" }} className="transition-colors duration-300 group-hover:text-[#1A201A]" aria-hidden="true" />
                      <span className="text-[0.6rem] tracking-[0.2em] uppercase" style={{ color: "#7B846F" }}>{label}</span>
                    </Link>
                  ))}
                </div>
                <p className="flex-1 text-center lg:text-left text-sm sm:text-base" style={{ color: "#4A554A" }}>
                  Shop classic styles from the past to today — <span className="font-medium" style={{ color: "#1A201A" }}>women, men &amp; kids</span>.
                </p>
                <Link to="/browse" className="btn-vintage" data-testid="cover-browse-btn">
                  Browse Collection <ChevronRight size={14} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Brand statement under the cover */}
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 pb-14 pt-6 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="label-eyebrow" style={{ color: "#697169" }}>1984 · 1996 · Today</div>
              <button type="button" onClick={() => setPromoOpen(true)} data-testid="promo-read-me-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-[0.14em] rounded-full transition-transform hover:-translate-y-0.5"
                style={{ background: "#2563EB", color: "#fff" }}>
                <Gift size={13} /> Read me &amp; win a $1000 Visa card
              </button>
            </div>
            <Dialog open={promoOpen} onOpenChange={setPromoOpen}>
              <DialogContent className="max-w-lg" data-testid="promo-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl flex items-center gap-2">
                    <Gift size={20} style={{ color: "#2563EB" }} /> Win a $1000 Visa gift card
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm leading-relaxed" style={{ color: "#3A423A" }}>
                  <p>
                    <span className="font-semibold">1.</span> We are a new online store, made for selling
                    high-end and near-mint clothing items.
                  </p>
                  <p>
                    <span className="font-semibold">2.</span> Items must not be heavily used or stained. People
                    selling items that are not within the guidelines will be automatically banned.
                  </p>
                  <p>
                    <span className="font-semibold">3.</span> High-end items can be slightly stained, if properly
                    noted and shown in the pictures, so buyers can make a clear decision on whether they think the
                    risk is worth taking on.
                  </p>
                  <p className="p-4 rounded-lg" style={{ background: "#EEF3FF", color: "#1A201A" }}>
                    <span className="font-semibold">4.</span> Tell all your friends about us! When we reach{" "}
                    <span className="font-semibold">10,000 listings</span>, we will be giving away a{" "}
                    <span className="font-semibold">$1000 Visa gift card</span> to one lucky member.
                  </p>
                  <button type="button" onClick={() => setPromoOpen(false)} data-testid="promo-close-btn"
                    className="btn-primary w-full justify-center">Got it — let's shop</button>
                </div>
              </DialogContent>
            </Dialog>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-[4.2rem] leading-[0.9] tracking-tighter mt-3" style={{ color: "#1A201A" }}>
              Classics <span className="font-cover font-normal italic" style={{ color: "#2563EB" }}>never</span> go out.
            </h2>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/browse?condition=used" data-testid="hero-shop-classic" className="btn-primary">Shop the Classics</Link>
              <Link to="/browse?condition=new" data-testid="hero-shop-new" className="btn-outline">Shop New Arrivals</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base leading-relaxed" style={{ color: "#4A554A" }}>
              From an <span className="font-medium" style={{ color: "#1A201A" }}>'80s knit</span> that still turns heads to a <span className="font-medium" style={{ color: "#1A201A" }}>'90s silhouette</span> reborn on the runway — and the <span className="font-medium" style={{ color: "#1A201A" }}>quiet luxury of 2026</span>. Your Classic Style is a marketplace for vintage &amp; contemporary pieces — clean, classic, and made to be worn again.
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 label-eyebrow" style={{ color: "#697169" }}>
              <Link to="/browse?product_type=apparel&dept=women" className="hover:text-[#1A201A]">For Her</Link>
              <span aria-hidden>·</span>
              <Link to="/browse?product_type=apparel&dept=men" className="hover:text-[#1A201A]">For Him</Link>
              <span aria-hidden>·</span>
              <Link to="/browse?product_type=apparel&dept=kids" className="hover:text-[#1A201A]">For the Little Ones</Link>
            </div>
          </div>
        </div>

        <div className="marquee hairline-t hairline-b py-4" style={{ background: "#F9F6F0" }}>
          <div className="marquee-track text-[0.72rem] uppercase tracking-[0.28em] whitespace-nowrap" style={{ color: "#4A554A" }}>
            {Array(4).fill("Vintage · Classic · Contemporary · Women · Men · Children · Handbags · Watches · Shoes · Jewelry · Timeless ·").map((s, i) => (
              <span key={`marquee-${i}`}>{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SEARCH — front & centre */}
      <section className="hairline-b" style={{ background: "#F9F6F0" }} data-testid="home-search">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-10 text-center">
          <div className="label-eyebrow" style={{ color: "#697169" }}>Find your piece</div>
          <form onSubmit={submitSearch} className="mt-4 flex items-center hairline bg-white mx-auto max-w-2xl"
            data-testid="home-search-form" role="search">
            <Search size={18} className="ml-4 text-neutral-400 shrink-0" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="home-search-input"
              placeholder="Search brands, colours, materials — e.g. leather, Levi's, wool…"
              className="flex-1 bg-transparent px-3 py-3.5 text-sm sm:text-base outline-none" />
            <button type="submit" data-testid="home-search-btn" className="btn-primary m-1.5">Search</button>
          </form>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs">
            <span className="text-neutral-400 uppercase tracking-[0.16em] text-[0.62rem]">Popular:</span>
            {["Denim", "Leather", "Wool", "Nike", "Silk"].map((t) => (
              <Link key={t} to={`/browse?q=${encodeURIComponent(t.toLowerCase())}`} className="chip"
                data-testid={`home-search-chip-${t.toLowerCase()}`}>{t}</Link>
            ))}
          </div>
        </div>
      </section>

      <TrustSpotlight />

      {/* TRENDING — most-liked this week */}
      {trending.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16" data-testid="home-trending">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-eyebrow" style={{ color: "#B91C1C" }}>Trending</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Most-liked this week.</h2>
            </div>
            <Link to="/browse" className="btn-klein hidden md:inline-flex" data-testid="trending-see-all">See more <ArrowUpRight size={14} /></Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 snap-x" data-testid="trending-row">
            {trending.map((it) => (
              <div key={it.id} className="w-[46%] sm:w-[30%] md:w-[22%] lg:w-[16%] shrink-0 snap-start" data-testid={`trending-card-${it.id}`}>
                <ProductCard item={it} />
                <div className="mt-1 h-4 text-[0.62rem] uppercase tracking-[0.14em] flex items-center gap-1" style={{ color: "#B91C1C" }}>
                  {it.week_likes > 0 && (<><Heart size={11} fill="#B91C1C" /> {it.week_likes} this week</>)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-eyebrow">02 · Categories</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3">Browse by wardrobe.</h2>
          </div>
          <Link to="/browse" className="btn-outline hidden md:inline-flex" data-testid="see-all-btn">See all <ArrowUpRight size={14}/></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {CATEGORY_TILES.map((c) => {
            const pv = catPreview[c.key];
            const realImg = pv?.image;
            return (
              <Link key={c.label} to={c.to} data-testid={`cat-${c.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                className="group relative aspect-[3/4] overflow-hidden hairline card-lift">
                {realImg ? (
                  <ImageThumb path={realImg} alt={c.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                ) : (
                  <img src={c.img} alt={c.label}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0"/>
                <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between text-white">
                  <div className="min-w-0">
                    <span className="font-display text-lg block leading-tight">{c.label}</span>
                    {pv?.count > 0 && (
                      <span className="text-[0.58rem] tracking-[0.16em] uppercase text-white/85"
                        data-testid={`cat-count-${c.key}`}>{pv.count} for sale</span>
                    )}
                  </div>
                  <ArrowUpRight size={16} className="shrink-0"/>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FOR SALE — homepage showcase (20 items; bumped pinned, rest random) */}
      {showcase.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pb-24" data-testid="home-showcase">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="label-eyebrow" style={{ color: "#2563EB" }}>03 · For Sale</div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3">Fresh on the floor.</h2>
              <p className="mt-2 text-sm text-neutral-500">A rotating pick of {showcase.length} pieces — featured sellers first.</p>
            </div>
            <Link to="/browse" className="btn-klein hidden md:inline-flex" data-testid="showcase-see-all">Shop everything <ArrowUpRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {showcase.map((it) => <ProductCard key={it.id} item={it} testid={`showcase-card-${it.id}`} />)}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link to="/browse" className="btn-outline" data-testid="showcase-see-all-mobile">Shop everything <ArrowUpRight size={14}/></Link>
          </div>
        </section>
      )}

      <StillInterested />
      <RecentlyViewed className="pb-20" />

      {/* HOW IT WORKS */}
      <section className="hairline-t hairline-b bg-neutral-50">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-3 gap-6">
          {[
            {n:"01", t:"Like by pattern", d:"Tap the heart on any item and save its color, pattern or style — we'll match new listings for you."},
            {n:"02", t:"Blue means new", d:"When a listing matches your saved criteria, your Saved tab lights up Klein Blue."},
            {n:"03", t:"Sellers meet you halfway", d:"Sellers see who liked their items and can offer a reduced price directly to interested buyers."},
          ].map((s)=>(
            <div key={s.n} className="hairline bg-white p-8">
              <div className="label-eyebrow">{s.n}</div>
              <h3 className="font-display text-2xl mt-3">{s.t}</h3>
              <p className="mt-3 text-sm text-neutral-500 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
